import React, { useMemo, useState } from "react";
import { useI18n } from "@/lib/i18n";
import { Play } from "lucide-react";

// Categories: 1=Floor, 2=Counter, 3=Interior, 4=Fireplace, 5=Detail, 6=Video
const ITEMS = [
  // Featured video first — cinematic
  { type: "video",  src: "/photos/v01.mp4", poster: "/photos/p14.jpg", cat: 6, t: "Полірування у процесі",  wide: true, ratio: "16/10" },
  { type: "photo",  src: "/photos/p14.jpg", cat: 3, t: "Преміальний інтерʼєр",   ratio: "3/4" },
  { type: "photo",  src: "/photos/p13.jpg", cat: 1, t: "Полірована підлога",     ratio: "3/4" },
  { type: "photo",  src: "/photos/p01.jpg", cat: 2, t: "Стільниця · мийка",      ratio: "3/4" },
  { type: "video",  src: "/photos/v02.mp4", poster: "/photos/p09.jpg", cat: 6, t: "Робочий процес",                     ratio: "3/4" },
  { type: "photo",  src: "/photos/p23.jpg", cat: 4, t: "Мармуровий камін",                                                ratio: "3/4" },
  { type: "photo",  src: "/photos/p15.jpg", cat: 3, t: "Інтерʼєр з підсвіткою", wide: true, ratio: "16/10" },
  { type: "photo",  src: "/photos/p09.jpg", cat: 1, t: "Мармурова плитка",       ratio: "3/4" },
  { type: "photo",  src: "/photos/p03.jpg", cat: 2, t: "Стільниця",              ratio: "3/4" },
  { type: "video",  src: "/photos/v03.mp4", poster: "/photos/p24.jpg", cat: 6, t: "Кристалізація підлоги",   ratio: "3/4" },
  { type: "photo",  src: "/photos/p16.jpg", cat: 4, t: "Камін · деталь",         ratio: "3/4" },
  { type: "photo",  src: "/photos/p24.jpg", cat: 1, t: "Геометричний візерунок", wide: true, ratio: "16/10" },
  { type: "photo",  src: "/photos/p26.jpg", cat: 1, t: "Фрагмент реставрації",   ratio: "3/4" },
  { type: "photo",  src: "/photos/p05.jpg", cat: 2, t: "Стільниця · полірування", ratio: "3/4" },
  { type: "video",  src: "/photos/v04.mp4", poster: "/photos/p11.jpg", cat: 6, t: "Реставрація підлоги",    ratio: "3/4" },
  { type: "photo",  src: "/photos/p11.jpg", cat: 1, t: "Підлога · фрагмент",     ratio: "3/4" },
  { type: "photo",  src: "/photos/p18.jpg", cat: 4, t: "Камін · класика",        ratio: "3/4" },
  { type: "video",  src: "/photos/v05.mp4", poster: "/photos/p21.jpg", cat: 6, t: "Дзеркальний блиск",       ratio: "3/4" },
  { type: "photo",  src: "/photos/p06.jpg", cat: 2, t: "Стільниця ванної",       ratio: "3/4" },
  { type: "photo",  src: "/photos/p02.jpg", cat: 5, t: "Фактура каменю",         ratio: "3/4" },
  { type: "photo",  src: "/photos/p21.jpg", cat: 4, t: "Камін · модерн",         wide: true, ratio: "16/10" },
  { type: "photo",  src: "/photos/p07.jpg", cat: 5, t: "Деталь полірування",     ratio: "3/4" },
  { type: "photo",  src: "/photos/p04.jpg", cat: 2, t: "Стільниця · підготовка", ratio: "3/4" },
  { type: "photo",  src: "/photos/p25.jpg", cat: 5, t: "Зовнішнє оздоблення",    ratio: "3/4" },
  { type: "photo",  src: "/photos/p12.jpg", cat: 5, t: "Мармурове підвіконня",   ratio: "3/4" },
  { type: "photo",  src: "/photos/p08.jpg", cat: 5, t: "Крупний план фактури",   ratio: "3/4" },
];

export default function Gallery() {
  const { t } = useI18n();
  const [tab, setTab] = useState(0);
  const [lightbox, setLightbox] = useState(null);

  // tabs indexed 0..6 : Усі, Підлоги, Стільниці, Інтерʼєри, Каміни, Деталі, Відео
  const items = useMemo(() => (tab === 0 ? ITEMS : ITEMS.filter((i) => i.cat === tab)), [tab]);

  return (
    <section id="gallery" data-testid="gallery-section" className="relative bg-onyx py-24 lg:py-40">
      <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
        {/* Heading */}
        <div className="grid grid-cols-12 gap-6 mb-12 lg:mb-16">
          <div className="col-span-12 lg:col-span-7">
            <div className="eyebrow mb-6 flex items-center gap-3">
              <span className="w-8 h-px bg-brass" /> {t.gallery.eyebrow}
            </div>
            <h2 className="font-display text-[2.6rem] sm:text-5xl lg:text-7xl text-bone leading-[1] tracking-tight">
              {t.gallery.title_1} <span className="italic-display text-brass">{t.gallery.title_2}</span>
            </h2>
          </div>
          <div className="col-span-12 lg:col-span-5 lg:col-start-8 flex items-end">
            <div className="flex flex-wrap gap-x-2 gap-y-2 lg:justify-end w-full">
              {t.gallery.tabs.map((label, i) => (
                <button
                  key={label}
                  data-testid={`gallery-tab-${i}`}
                  onClick={() => setTab(i)}
                  className={`px-3 py-2 text-[0.72rem] tracking-[0.18em] uppercase border transition-all duration-500 ${
                    tab === i ? "border-brass text-brass" : "border-bone/15 text-bone/60 hover:text-bone hover:border-bone/40"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-10">
          {items.map((it, i) => (
            <figure
              key={`${it.src}-${i}`}
              data-testid={`gallery-item-${i}`}
              className={`hover-zoom relative overflow-hidden bg-charcoal cursor-zoom-in group ${it.wide ? "lg:col-span-2" : ""}`}
              style={{ aspectRatio: it.ratio }}
              onClick={() => setLightbox(it)}
            >
              {it.type === "video" ? (
                <>
                  <img
                    src={it.poster}
                    alt={it.t}
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-1000"
                  />
                  <div className="absolute inset-0 grid place-items-center">
                    <div className="w-16 h-16 rounded-full bg-brass/95 grid place-items-center shadow-xl group-hover:scale-110 transition-transform duration-500">
                      <Play className="w-6 h-6 text-onyx ml-1" fill="currentColor" />
                    </div>
                  </div>
                  <div className="absolute top-4 left-4 px-2 py-1 bg-onyx/80 text-[0.6rem] tracking-[0.2em] uppercase text-brass">Відео</div>
                </>
              ) : (
                <img
                  src={it.src}
                  alt={it.t}
                  loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-1000"
                />
              )}
              <figcaption className="absolute inset-x-0 bottom-0 p-5 bg-gradient-to-t from-onyx via-onyx/60 to-transparent opacity-90 group-hover:opacity-100 transition-opacity">
                <div className="text-[0.65rem] tracking-[0.2em] uppercase text-brass">— {String(i + 1).padStart(2, "0")}</div>
                <div className="font-display text-xl text-bone mt-1">{it.t}</div>
              </figcaption>
            </figure>
          ))}
        </div>

        {/* Lightbox */}
        {lightbox && (
          <div
            data-testid="lightbox"
            onClick={() => setLightbox(null)}
            className="fixed inset-0 z-[90] bg-onyx/95 backdrop-blur-md grid place-items-center p-6 cursor-zoom-out"
          >
            <button
              data-testid="lightbox-close"
              onClick={() => setLightbox(null)}
              className="absolute top-6 right-6 text-bone/70 hover:text-brass text-[0.7rem] tracking-[0.2em] uppercase"
            >
              Закрити ✕
            </button>
            {lightbox.type === "video" ? (
              <video
                src={lightbox.src}
                controls
                autoPlay
                playsInline
                className="max-w-[92vw] max-h-[85vh]"
                onClick={(e) => e.stopPropagation()}
              />
            ) : (
              <img src={lightbox.src} alt={lightbox.t} className="max-w-[92vw] max-h-[85vh] object-contain" onClick={(e) => e.stopPropagation()} />
            )}
            <div className="absolute bottom-6 left-1/2 -translate-x-1/2 font-display text-xl text-bone">{lightbox.t}</div>
          </div>
        )}
      </div>
    </section>
  );
}
